import type React from "react";
import { useState, useEffect, useRef } from "react";
import { ResponsiveDialog } from "@/components/common/ResponsiveDialog";

interface Feature {
	title: string;
	description: string;
	icon: string;
	details: string[];
}

const Features: React.FC = () => {
	const [isVisible, setIsVisible] = useState(false);
	const featuresRef = useRef<HTMLDivElement>(null);

	const features: Feature[] = [
		{
			title: "アウトプットできる環境",
			description:
				"自分の学んだことをすぐに実践し、アウトプットすることができます。",
			icon: "💡",
			details: [
				"定期的なプロジェクト発表会",
				"オンラインポートフォリオの作成支援",
				"メンターによるフィードバック",
			],
		},
		{
			title: "初心者から上級者まで歓迎",
			description: "初心者から上級者まで、どんなレベルの方でも歓迎します。",
			icon: "👨‍🎓",
			details: [
				"レベル別のカリキュラム",
				"個別学習プランの作成",
				"進捗に応じた課題の提供",
			],
		},
		{
			title: "年齢制限なし",
			description: "学生も社会人も意欲がある方はどなたでも参加できます。",
			icon: "🌍",
			details: [
				"多様な年齢層のコミュニティ",
				"年齢に応じた学習アプローチ",
				"世代を超えた交流イベント",
			],
		},
		{
			title: "オンラインからも参加可能",
			description: "オンラインでも参加できるので、場所を問わず参加できます。",
			icon: "💻",
			details: [
				"ライブストリーミング授業",
				"オンデマンド学習コンテンツ",
				"オンラインディスカッションフォーラム",
			],
		},
		{
			title: "メンター常駐",
			description: "困ったときにはメンターが学習の計画を一緒に考えます。",
			icon: "👨‍🏫",
			details: [
				"1対1のメンタリングセッション",
				"グループメンタリング",
				"技術相談サポート",
			],
		},
		{
			title: "参加費無料",
			description: "参加費は無料なので、気軽に参加できます。",
			icon: "💰",
			details: [
				"無料の学習教材",
				"オープンソースプロジェクトへの参加",
				"コミュニティ主導のワークショップ",
			],
		},
	];

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setIsVisible(true);
						observer.disconnect();
						break;
					}
				}
			},
			{ threshold: 0.1 },
		);

		if (featuresRef.current) {
			observer.observe(featuresRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<section className="py-16 bg-gray-100">
			<div className="container mx-auto">
				<h2 className="text-3xl font-bold text-center mb-12">
					自分の可能性を広げるチャンスです
				</h2>
				<div
					ref={featuresRef}
					className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
				>
					{features.map((feature) => (
						<ResponsiveDialog
							key={feature.title}
							trigger={
								<button
									type="button"
									className="bg-white p-6 rounded-lg shadow-md cursor-pointer transition-transform duration-300 hover:-translate-y-2 text-left w-full"
								>
									<div
										className={`text-center text-5xl mb-4 ${isVisible ? "animate-bounce-once" : ""}`}
									>
										{feature.icon}
									</div>
									<h3 className="text-center text-xl font-semibold mb-2">
										{feature.title}
									</h3>
									<p className="text-gray-600">{feature.description}</p>
								</button>
							}
							title={feature.title}
							description={feature.description}
						>
							<ul className="list-disc pl-5 mt-4">
								{feature.details.map((detail) => (
									<li key={detail} className="mb-2">
										{detail}
									</li>
								))}
							</ul>
						</ResponsiveDialog>
					))}
				</div>
			</div>
		</section>
	);
};

export default Features;
